import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// MIGRATION: reads via PostgREST; writes go through authWrite Lambda so
// every subscription create/edit/cancel and every CSV import row lands
// in auth_audit_log.
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { useToast } from '../lib/Toast';
import { humanizeError } from '../lib/humanizeError';
import { can } from '../lib/permissions';
import { fetchXeroMatches, findMatchForSub, varianceChipStyle, isAlreadyApproved, clearXeroMatchCache } from '../lib/subsXeroMatch';
import { downloadXlsx } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';

const CATEGORIES = ['software','email_licence','advertising_platform','ecommerce_platform','analytics','design','ai_tools','accounting','legal','hr','domains_hosting','contractor_retainer','membership','other'];
const CAT_LABELS = { software: 'Software', email_licence: 'Email Licence', advertising_platform: 'Advertising', ecommerce_platform: 'eCommerce', analytics: 'Analytics', design: 'Design', ai_tools: 'AI Tools', accounting: 'Accounting', legal: 'Legal', hr: 'HR', domains_hosting: 'Domains', contractor_retainer: 'Contractor', membership: 'Membership', other: 'Other' };
const CYCLES = ['monthly', 'quarterly', 'annually', 'custom_months', 'on_use'];
const CURRENCIES = ['AUD', 'USD', 'EUR', 'GBP', 'CAD', 'HKD', 'SGD', 'JPY'];
// Aligned with migration 86 payment_method_kind CHECK constraint.
// Legacy values (amex_personal, wire) preserved in DB but mapped at write time.
const PAYMENTS = ['amex_business', 'visa_business', 'mastercard_business', 'direct_debit', 'wire_nab', 'wire_other', 'paypal', 'other'];
const PAY_LABELS = {
  amex_business: 'Amex Biz', amex_personal: 'Amex Personal',
  visa_business: 'Visa Biz', mastercard_business: 'Mastercard Biz',
  direct_debit: 'Direct Debit',
  wire_nab: 'Wire (NAB)', wire_other: 'Wire (other)', wire: 'Wire',
  paypal: 'PayPal', other: 'Other'
};
// Map any payment_method (legacy or current) → payment_method_kind (migration 86 enum).
// Returns null if input is empty/unknown so the column stays NULL rather than failing the CHECK.
function toPaymentKind(method) {
  if (!method) return null;
  const m = String(method).toLowerCase();
  if (m === 'amex_business' || m === 'visa_business' || m === 'mastercard_business'
      || m === 'direct_debit' || m === 'wire_nab' || m === 'wire_other'
      || m === 'paypal' || m === 'other') return m;
  if (m === 'amex_personal') return 'other';     // not in enum
  if (m === 'wire') return 'wire_other';          // legacy unspecified wire
  return 'other';
}

// P0 hotfix 2026-05-29 — Xero matching panel read-only.
// Background: subscription_events.event_type has a CHECK constraint that
// accepts ('created','charged','price_change','cycle_change','owner_change',
// 'status_change','note_added','attachment_added','cancelled','reactivated').
// The Approve-as-charge path writes 'charge' (not 'charged') and the Adopt
// path triggers edit_<field> events — both rejected by the constraint and
// surfaced to the user as the "Invalid value" modal. Suggested-estimate math
// also under-counts when Xero coverage is partial (most annual subs).
// Hiding the write paths until matching rules and event_type values are
// reconciled. Read-only display stays live.
const XERO_MATCH_READONLY = true;

// When matching IS re-enabled later, the suggested estimate is unsafe for
// any of these reasons. Hides the Adopt button regardless of READONLY.
const UNSAFE_XERO_VENDOR_PATTERNS = /shopify|google|paddle|paypal|stripe|claude|amex|american express|square|wise|klarna|afterpay/i;
function isUnsafeMatchSuggestion(match, drawer) {
  if (!match) return true;
  if (match.variance_status === 'review' || match.variance_status === 'conflict') return true;
  if (match.conflicts && match.conflicts.length > 0) return true;
  if (drawer?.billing_cycle === 'annually') return true;
  const charges = Number(match.matched_xero_charge_count || 0);
  if (drawer?.billing_cycle === 'quarterly' && charges < 2) return true;
  if (drawer?.billing_cycle === 'monthly' && charges < 3) return true;
  if (match.matched_xero_contact && UNSAFE_XERO_VENDOR_PATTERNS.test(match.matched_xero_contact)) return true;
  return false;
}

// 2026-05-29 P0 UX hardening — mirrors the saveSub validators so the Save
// button can be disabled before click instead of failing via toast after.
// If you change the validators in saveSub, change these too.
function isAmountValid(form) {
  if (!form) return false;
  if (form.variable_cost) {
    const mn = parseFloat(form.amount_min);
    const mx = parseFloat(form.amount_max);
    return mn > 0 && mx > 0 && mx >= mn;
  }
  return parseFloat(form.amount) > 0;
}

function fmtMoney(n, cur = 'AUD') { return `${cur === 'AUD' ? 'A' : ''}$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '--'; }
function fmtDateTime(d) { return d ? new Date(d).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '--'; }

// Edit-diff helpers (Phase D, 2026-05-28) — turn before/after values into
// human-readable audit-log notes like "Amount changed from AUD 1,165 to AUD 1,250".
const DIFF_FIELD_LABEL = {
  vendor_name: 'Vendor',
  product_description: 'Description',
  category: 'Category',
  status: 'Status',
  billing_cycle: 'Cycle',
  amount: 'Amount',
  amount_min: 'Amount min',
  amount_max: 'Amount max',
  currency: 'Currency',
  payment_method: 'Payment method',
  next_renewal_date: 'Next renewal',
  started_on: 'Started on',
  auto_renew: 'Auto-renew',
  variable_cost: 'Variable cost',
  notes: 'Notes',
};
const DIFF_TRACKED_FIELDS = Object.keys(DIFF_FIELD_LABEL);

function fmtDiffValue(field, val) {
  if (val == null || val === '') return '(empty)';
  if (field === 'amount' || field === 'amount_min' || field === 'amount_max') return Number(val).toFixed(2);
  if (field === 'next_renewal_date' || field === 'started_on') return fmtDate(val);
  if (field === 'auto_renew' || field === 'variable_cost') return val ? 'Yes' : 'No';
  if (field === 'notes' || field === 'product_description') {
    const s = String(val);
    return s.length > 60 ? s.slice(0, 60) + '…' : s;
  }
  return String(val);
}

function computeFieldDiffs(original, edited) {
  const diffs = [];
  for (const field of DIFF_TRACKED_FIELDS) {
    const prev = original?.[field];
    const next = edited?.[field];
    const prevS = prev == null ? '' : String(prev);
    const nextS = next == null ? '' : String(next);
    if (prevS === nextS) continue;
    // Numeric tolerance — 100 vs "100.00" should NOT count as a change.
    if (field === 'amount' || field === 'amount_min' || field === 'amount_max') {
      const pn = parseFloat(prevS); const nn = parseFloat(nextS);
      if (!isNaN(pn) && !isNaN(nn) && Math.abs(pn - nn) < 0.005) continue;
    }
    diffs.push({
      field,
      prev,
      next,
      displayNote: `${DIFF_FIELD_LABEL[field]} changed from ${fmtDiffValue(field, prev)} to ${fmtDiffValue(field, next)}`
    });
  }
  return diffs;
}

export default function Subscriptions({ session }) {
  const toast = useToast();
  const canWrite = can(session, 'finance_subs_write');
  const [subs, setSubs] = useState([]);
  const [fxRates, setFxRates] = useState({});
  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [drawer, setDrawer] = useState(null);
  const [drawerEvents, setDrawerEvents] = useState([]);
  const [newSub, setNewSub] = useState(null);
  // Delete-record confirmation modal (separate from drawer + newSub form).
  // When non-null, holds the subscription object the user wants to delete.
  // Mo, 2026-05-28: distinct from "Mark as cancelled" — delete removes the
  // internal dashboard record + its event timeline (cascade), does NOT
  // touch the vendor's actual subscription.
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Comments & activity input (Phase B, 2026-05-28).
  const [commentText, setCommentText] = useState('');
  // Actual-charge add form (Phase C, 2026-05-28). When non-null, holds the
  // draft charge fields. The "+ Add charge" button opens it pre-filled from
  // the current drawer's currency / payment_method.
  const [chargeForm, setChargeForm] = useState(null);
  // Xero match data (R1+R2+R3, 2026-05-29). Fetched on first drawer open
  // and cached in subsXeroMatch.js. Holds the full Lambda payload; the
  // drawer renders the slice for the current subscription only.
  const [xeroMatchesData, setXeroMatchesData] = useState(null);
  const [xeroMatchesLoading, setXeroMatchesLoading] = useState(false);
  const [xeroMatchesError, setXeroMatchesError] = useState(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [staff, setStaff] = useState([]);

  const loadSubs = useCallback(async () => { const { data } = await aws.from('subscriptions').select('*').order('vendor_name'); setSubs(data || []); }, []);
  const loadFx = useCallback(async () => { const { data } = await aws.from('fx_rates').select('*'); const map = {}; (data || []).forEach(r => { map[r.currency_code] = r.rate_to_aud; }); setFxRates(map); }, []);

  // Mig 139 (C1, 2026-05-10): staff_directory dropped `code`. We still
  // need the code → name map for resolving actor_code on
  // subscription_events comments / system events. (Owner UI was removed
  // 2026-05-28; the staff list is no longer used for an owner dropdown.)
  // admin_staff_directory() RPC returns the code for admin sessions.
  // Subscriptions is admin-gated upstream.
  useEffect(() => { loadSubs(); loadFx(); aws.rpc('admin_staff_directory').then(({ data }) => setStaff((data || []).filter(s => s.active))); }, []);

  function monthlyEquiv(s) {
    if (s.status !== 'active' && s.status !== 'trial') return 0;
    const fx = fxRates[s.currency] || 1;
    const aud = s.amount * fx;
    if (s.billing_cycle === 'monthly') return aud;
    if (s.billing_cycle === 'quarterly') return aud / 3;
    if (s.billing_cycle === 'annually') return aud / 12;
    if (s.billing_cycle === 'custom_months' && s.custom_cycle_months > 0) return aud / s.custom_cycle_months;
    return aud; // on_use: treat as monthly
  }

  const activeSubs = useMemo(() => subs.filter(s => s.status === 'active' || s.status === 'trial'), [subs]);
  const cancelledSubs = useMemo(() => subs.filter(s => s.status === 'cancelled'), [subs]);
  const monthlyBurn = useMemo(() => activeSubs.reduce((sum, s) => sum + monthlyEquiv(s), 0), [activeSubs, fxRates]);
  const annualBurn = monthlyBurn * 12;
  const renewals30d = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() + 30);
    return activeSubs.filter(s => s.next_renewal_date && new Date(s.next_renewal_date) <= cutoff);
  }, [activeSubs]);
  const cancelledSavings = useMemo(() => cancelledSubs.reduce((sum, s) => { const fx = fxRates[s.currency] || 1; return sum + s.amount * fx; }, 0), [cancelledSubs, fxRates]);

  const displayed = useMemo(() => {
    let list = statusFilter === 'cancelled' ? cancelledSubs : activeSubs;
    if (catFilter !== 'all') list = list.filter(s => s.category === catFilter);
    if (search) { const q = search.toLowerCase(); list = list.filter(s => (s.vendor_name || '').toLowerCase().includes(q) || (s.product_description || '').toLowerCase().includes(q) || (s.notes || '').toLowerCase().includes(q)); }
    return [...list].sort((a, b) => monthlyEquiv(b) - monthlyEquiv(a));
  }, [subs, catFilter, statusFilter, search, fxRates]);

  // Variable cost subs
  const variableCostSubs = useMemo(() => activeSubs.filter(s => s.variable_cost), [activeSubs]);
  const variableCostBurn = useMemo(() => variableCostSubs.reduce((sum, s) => sum + monthlyEquiv(s), 0), [variableCostSubs, fxRates]);
  // Renew <14d (the prototype's KPI window)
  const renewals14d = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() + 14);
    return activeSubs.filter(s => s.next_renewal_date && new Date(s.next_renewal_date) <= cutoff);
  }, [activeSubs]);

  // CSV Import
  const csvRef = useRef(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);

  function handleCsvFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) return toast.error('CSV looks empty', 'It needs a header row and at least one row of data underneath.');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z_]/g, ''));
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const row = {};
        headers.forEach((h, j) => { row[h] = vals[j] || ''; });
        if (row.vendor_name || row.vendor) rows.push({
          vendor_name: row.vendor_name || row.vendor || row.name || '',
          product_description: row.product_description || row.description || '',
          category: row.category || 'software',
          status: row.status || 'active',
          billing_cycle: row.billing_cycle || row.cycle || 'monthly',
          amount: parseFloat(row.amount || row.price || '0') || 0,
          currency: row.currency || 'AUD',
          payment_method: row.payment_method || 'other',
          variable_cost: row.variable_cost === 'true' || row.variable_cost === '1',
          notes: row.notes || '',
        });
      }
      if (rows.length === 0) return toast.error('No valid rows found', 'The file parsed but every row was missing a vendor name. Check the column headers match the template.');
      setCsvPreview(rows);
    };
    reader.readAsText(file);
  }

  async function importCsv() {
    if (!csvPreview?.length) return;
    setCsvImporting(true);
    // Dual-write: every CSV row sets BOTH payment_method (legacy text) and
    // payment_method_kind (migration 86 typed column). The legacy column is
    // kept until the cleanup migration drops it.
    const toInsert = csvPreview.map(r => ({
      ...r,
      payment_method_kind: toPaymentKind(r.payment_method),
      created_by: session?.code || 'csv_import',
      auto_renew: true
    }));
    try {
      // authWrite accepts an array payload for batch insert.
      await authWrite(session, 'subscriptions', 'insert', toInsert);
    } catch (e) {
      setCsvImporting(false);
      const { title, body } = humanizeError(e, 'CSV import failed');
      toast.error(title, body);
      return;
    }
    setCsvImporting(false);
    setCsvPreview(null);
    loadSubs();
    toast.success(`Imported ${csvPreview.length} subscription${csvPreview.length === 1 ? '' : 's'}`);
  }

  async function openDrawer(s) {
    setDrawer(s);
    const { data } = await aws.from('subscription_events').select('*').eq('subscription_id', s.id).order('created_at', { ascending: false });
    setDrawerEvents(data || []);
    // Fire-and-forget Xero match fetch. Helper caches client-side (5 min)
    // and dedupes concurrent calls, so multiple drawer opens in a row hit
    // the same in-flight promise. Failure is non-fatal — the drawer still
    // renders, the Matched Xero payments section shows the error inline.
    setXeroMatchesError(null);
    if (!xeroMatchesData) {
      setXeroMatchesLoading(true);
      fetchXeroMatches(session)
        .then(d => setXeroMatchesData(d))
        .catch(e => setXeroMatchesError(String(e?.message || e).slice(0, 300)))
        .finally(() => setXeroMatchesLoading(false));
    }
  }

  async function saveSub() {
    if (!newSub?.vendor_name?.trim()) {
      toast.error('Vendor name required', 'Subscriptions need a vendor — give it a name and try again.');
      return;
    }
    // Amount handling: variable-cost subs need a Min and Max; fixed-cost
    // subs need a single Amount. Compute the canonical `amount` as the
    // midpoint for variable so monthlyEquiv() math still works without
    // changes. amount_min / amount_max are stored alongside for display
    // and future range-aware reporting.
    let amountNum, amountMin = null, amountMax = null;
    if (newSub.variable_cost) {
      amountMin = parseFloat(newSub.amount_min);
      amountMax = parseFloat(newSub.amount_max);
      if (!amountMin || amountMin <= 0) {
        toast.error('Min amount required', 'Variable-cost subscriptions need a minimum amount above $0.');
        return;
      }
      if (!amountMax || amountMax <= 0) {
        toast.error('Max amount required', 'Variable-cost subscriptions need a maximum amount above $0.');
        return;
      }
      if (amountMax < amountMin) {
        toast.error('Range invalid', 'The Max amount must be greater than or equal to the Min.');
        return;
      }
      amountNum = (amountMin + amountMax) / 2;       // midpoint for burn math
    } else {
      amountNum = parseFloat(newSub.amount);
      if (!amountNum || amountNum <= 0) {
        toast.error('Amount required', 'Subscriptions must cost more than $0. Type the price into the Amount field.');
        return;
      }
    }
    // Dual-write: keep legacy `payment_method` (text) AND set new
    // `payment_method_kind` (migration 86 typed column). Drop legacy in a
    // later cleanup migration.
    const payload = {
      ...newSub,
      vendor_name: newSub.vendor_name.trim(),
      amount: amountNum,
      amount_min: amountMin,
      amount_max: amountMax,
      payment_method_kind: toPaymentKind(newSub.payment_method),
      created_by: session?.code || 'mo'
    };
    delete payload._isNew;
    // Snapshot of pre-edit values, used below to emit per-field audit
    // events after the update succeeds. Strip from the wire payload so
    // it never reaches the SET clause.
    const originalSnapshot = newSub._original;
    delete payload._original;
    // Don't ship internal/PK fields to the SET clause on update; the row
    // is identified by the WHERE match. (id is also untouchable, and
    // sending created_at/updated_at would clobber timestamps.)
    delete payload.id;
    delete payload.created_at;
    delete payload.updated_at;
    try {
      if (newSub._isNew) {
        await authWrite(session, 'subscriptions', 'insert', payload);
      } else {
        await authWrite(session, 'subscriptions', 'update', payload,
          { column: 'id', value: newSub.id });
        // Phase D (2026-05-28): emit one subscription_events row per
        // field whose value changed. Best-effort — audit failure must
        // NOT mask the successful update from the user.
        if (originalSnapshot) {
          const diffs = computeFieldDiffs(originalSnapshot, payload);
          const today = new Date().toISOString().slice(0, 10);
          for (const d of diffs) {
            try {
              await authWrite(session, 'subscription_events', 'insert', {
                subscription_id: newSub.id,
                event_type: `edit_${d.field}`,
                event_date: today,
                previous_value: d.prev == null ? null : String(d.prev),
                new_value:      d.next == null ? null : String(d.next),
                note: d.displayNote,
                actor_code: session?.code || null
              });
            } catch (auditErr) {
              console.warn('[subscriptions] audit event write failed for field', d.field, auditErr);
            }
          }
        }
      }
    } catch (e) {
      const { title, body } = humanizeError(e, newSub._isNew ? 'Could not add subscription' : 'Could not update subscription');
      toast.error(title, body);
      return;
    }
    setNewSub(null); loadSubs();
    toast.success(newSub._isNew ? 'Subscription added' : 'Subscription updated');
  }

  function handleDownload() {
    const columns = [
      { label: 'Vendor', width: 24, value: r => r.vendor_name || '' },
      { label: 'Description', width: 26, value: r => r.product_description || '' },
      { label: 'Category', width: 16, value: r => CAT_LABELS[r.category] || r.category || '' },
      { label: 'Amount', width: 12, numFmt: '#,##0.00', value: r => r.variable_cost ? '' : Number(r.amount || 0) },
      { label: 'Amount Min', width: 12, numFmt: '#,##0.00', value: r => r.variable_cost ? Number(r.amount_min || 0) : '' },
      { label: 'Amount Max', width: 12, numFmt: '#,##0.00', value: r => r.variable_cost ? Number(r.amount_max || 0) : '' },
      { label: 'Currency', width: 10, value: r => r.currency || '' },
      { label: 'Cycle', width: 14, value: r => r.billing_cycle === 'custom_months' ? `${r.custom_cycle_months}mo` : r.billing_cycle || '' },
      { label: 'Monthly (AUD)', width: 14, numFmt: '"A$"#,##0', value: r => Math.round(monthlyEquiv(r)) },
      { label: 'Next Renewal', width: 14, value: r => r.next_renewal_date || '' },
      { label: 'Started', width: 14, value: r => r.started_on || '' },
      { label: 'Payment', width: 16, value: r => PAY_LABELS[r.payment_method_kind || r.payment_method] || r.payment_method_kind || r.payment_method || '' },
      { label: 'Status', width: 12, value: r => r.status || '' },
    ];
    const filename = `FON_Subscriptions_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsx(filename, 'Subscriptions', columns, displayed);
    logExportActivity(session, 'Subscriptions', filename);
  }

  return (
    <div>
      {/* KPI Grid — 5 tiles per Finance §3.6 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Active', value: activeSubs.length, sub: 'Subscriptions running' },
          { label: 'Monthly run-rate', value: fmtMoney(monthlyBurn), sub: 'All in, AUD' },
          { label: 'Annualised', value: fmtMoney(annualBurn), sub: '12× run-rate' },
          { label: 'Variable', value: variableCostSubs.length, sub: variableCostSubs.length ? 'Watch usage spikes' : 'None on watch' },
          { label: 'Renew < 14d', value: renewals14d.length, sub: renewals14d.length ? fmtMoney(renewals14d.reduce((s, r) => s + monthlyEquiv(r), 0)) + '/mo' : 'Coming up' },
        ].map(t => (
          <div key={t.label} style={{ background: '#FAFAF8', border: '1px solid #EFEDE7', borderRadius: 12, padding: '14px 18px' }}>
            <div style={{ fontSize: 10, fontWeight: 500, color: '#8B9094', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#0F1419', marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{t.value}</div>
            {t.sub && <div style={{ fontSize: 11, color: '#5C6166', marginTop: 2 }}>{t.sub}</div>}
          </div>
        ))}
      </div>

      {/* Variable Cost Watch */}
      {variableCostSubs.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '12px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 16 }}>⚠</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e' }}>Variable cost watch — {fmtMoney(variableCostBurn)}/mo across {variableCostSubs.length} subs</div>
            <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 2 }}>{variableCostSubs.map(s => s.vendor_name).join(', ')}</div>
          </div>
        </div>
      )}

      {/* Filters + Add */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: '5px 10px', fontSize: 11, borderRadius: 6, border: '1px solid #e4e4e7', fontFamily: 'inherit', color: '#52525b' }}>
          <option value="active">Active</option><option value="cancelled">Cancelled</option>
        </select>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={{ padding: '5px 10px', fontSize: 11, borderRadius: 6, border: '1px solid #e4e4e7', fontFamily: 'inherit', color: '#52525b' }}>
          <option value="all">All Categories</option>{CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search subscriptions..." style={{ padding: '5px 10px', fontSize: 11, borderRadius: 6, border: '1px solid #e4e4e7', width: 200, fontFamily: 'inherit' }} />
        <div style={{ flex: 1 }} />
        <input ref={csvRef} type="file" accept=".csv" hidden onChange={handleCsvFile} />
        <button onClick={() => csvRef.current?.click()} style={{ padding: '6px 14px', fontSize: 11, fontWeight: 500, borderRadius: 8, border: '1px solid #e4e4e7', background: '#fff', color: '#52525b', cursor: 'pointer', fontFamily: 'inherit' }}>Import CSV</button>
        {canWrite && <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>}
        {canWrite && <button onClick={() => setNewSub({ _isNew: true, vendor_name: '', category: 'software', status: 'active', billing_cycle: 'monthly', amount: '', amount_min: '', amount_max: '', currency: 'AUD', payment_method: 'amex_business', auto_renew: true, variable_cost: false })} style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>+ Add Subscription</button>}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, overflowX: 'auto', marginBottom: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 800 }}>
          <thead><tr>{['Vendor', 'Category', 'Amount', 'Cycle', 'Monthly AUD', 'Next Renewal', 'Payment', 'Status'].map(h => <th key={h} style={{ textAlign: ['Amount', 'Monthly AUD'].includes(h) ? 'right' : 'left', fontSize: 10, fontWeight: 500, color: '#a1a1aa', padding: '10px 14px', borderBottom: '1px solid #f4f4f5', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>)}</tr></thead>
          <tbody>{displayed.length === 0 ? (
            <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#a1a1aa' }}>No subscriptions found.</td></tr>
          ) : displayed.map(s => {
            const me = monthlyEquiv(s);
            const renewSoon = s.next_renewal_date && new Date(s.next_renewal_date) <= new Date(Date.now() + 30 * 86400000);
            return (
              <tr key={s.id} onClick={() => openDrawer(s)} style={{ cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = '#fafafa'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '10px 14px', borderBottom: '1px solid #fafafa' }}>
                  <div style={{ fontWeight: 500, color: '#0a0a0a' }}>{s.vendor_name}</div>
                  {s.product_description && <div style={{ fontSize: 10, color: '#a1a1aa' }}>{s.product_description}</div>}
                </td>
                <td style={{ padding: '10px 14px', borderBottom: '1px solid #fafafa' }}><span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 999, background: '#f4f4f5', color: '#52525b' }}>{CAT_LABELS[s.category] || s.category}</span></td>
                <td style={{ padding: '10px 14px', borderBottom: '1px solid #fafafa', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {s.variable_cost && s.amount_min != null && s.amount_max != null
                    ? <>{s.currency} {Number(s.amount_min).toLocaleString(undefined, { minimumFractionDigits: 2 })}–{Number(s.amount_max).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
                    : <>{s.currency} {Number(s.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</>}
                </td>
                <td style={{ padding: '10px 14px', borderBottom: '1px solid #fafafa' }}><span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 999, background: '#ede9fe', color: '#7c3aed' }}>{s.billing_cycle === 'custom_months' ? `${s.custom_cycle_months}mo` : s.billing_cycle}</span></td>
                <td style={{ padding: '10px 14px', borderBottom: '1px solid #fafafa', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(me)}</td>
                <td style={{ padding: '10px 14px', borderBottom: '1px solid #fafafa', color: renewSoon ? '#dc2626' : '#a1a1aa', fontWeight: renewSoon ? 600 : 400, fontSize: 11 }}>{fmtDate(s.next_renewal_date)}</td>
                <td style={{ padding: '10px 14px', borderBottom: '1px solid #fafafa' }}><span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 999, background: '#f4f4f5', color: '#52525b' }}>{PAY_LABELS[s.payment_method_kind || s.payment_method] || s.payment_method_kind || s.payment_method}{s.payment_method_last4 ? ` ****${s.payment_method_last4}` : ''}</span></td>
                <td style={{ padding: '10px 14px', borderBottom: '1px solid #fafafa' }}><span style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: s.status === 'active' ? '#dcfce7' : s.status === 'trial' ? '#dbeafe' : s.status === 'paused' ? '#fef3c7' : '#f4f4f5', color: s.status === 'active' ? '#16a34a' : s.status === 'trial' ? '#2563eb' : s.status === 'paused' ? '#d97706' : '#a1a1aa' }}>{s.status}</span></td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>

      {/* Drawer */}
      {drawer && (<>
        <div onClick={() => setDrawer(null)} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.3)' }} />
        <aside style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 560, maxWidth: '90vw', zIndex: 201, background: '#fff', boxShadow: '-8px 0 32px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #e4e4e7', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#0a0a0a' }}>{drawer.vendor_name}</div>
                <div style={{ fontSize: 12, color: '#a1a1aa', marginTop: 2 }}>{drawer.product_description}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed', marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(monthlyEquiv(drawer))}/mo</div>
              </div>
              <button onClick={() => setDrawer(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#a1a1aa' }}>x</button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Amount', value: drawer.variable_cost && drawer.amount_min != null && drawer.amount_max != null
                  ? `${drawer.currency} ${Number(drawer.amount_min).toFixed(2)}–${Number(drawer.amount_max).toFixed(2)} (variable)`
                  : `${drawer.currency} ${Number(drawer.amount).toFixed(2)}` },
                { label: 'Cycle', value: drawer.billing_cycle },
                { label: 'Category', value: CAT_LABELS[drawer.category] || drawer.category },
                { label: 'Payment', value: PAY_LABELS[drawer.payment_method_kind || drawer.payment_method] || drawer.payment_method_kind || drawer.payment_method },
                { label: 'Next Renewal', value: fmtDate(drawer.next_renewal_date) },
                { label: 'Started', value: fmtDate(drawer.started_on) },
                { label: 'Auto-renew', value: drawer.auto_renew ? 'Yes' : 'No' },
              ].map(f => (
                <div key={f.label} style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase' }}>{f.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a', marginTop: 2 }}>{f.value}</div>
                </div>
              ))}
            </div>
            {drawer.notes && <div style={{ fontSize: 12, color: '#52525b', lineHeight: 1.5, padding: '10px 14px', background: '#fafafa', borderRadius: 8, marginBottom: 16 }}>{drawer.notes}</div>}

            {/* Matched Xero payments (R1+R2+R3, 2026-05-29).
                On-demand Lambda call (PIN-validated, 15 min Lambda-side
                cache, 5 min client-side cache). Surfaces variance, top
                Xero transactions, conflict warnings. Approve writes a
                charge event into subscription_events. Adopt opens Edit
                modal with suggested amount pre-filled. Read-only users
                see the data but no buttons. Failure is rendered inline,
                never breaks the drawer. */}
            {(() => {
              const match = xeroMatchesData ? findMatchForSub(xeroMatchesData, drawer.id) : null;
              const chip = match ? varianceChipStyle(match.variance_status) : null;
              return (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a' }}>Matched Xero payments</div>
                    {xeroMatchesLoading && <div style={{ fontSize: 11, color: '#a1a1aa' }}>loading…</div>}
                    {match && chip && (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: chip.bg, border: `1px solid ${chip.border}`, color: chip.color }}>{chip.label}</span>
                    )}
                  </div>

                  {XERO_MATCH_READONLY && (
                    <div style={{ fontSize: 11, color: '#7c2d12', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                      Review only — approvals and estimate updates are paused while matching rules are being verified.
                    </div>
                  )}

                  {xeroMatchesError && (
                    <div style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>
                      Could not load Xero matches: {xeroMatchesError}
                    </div>
                  )}

                  {!xeroMatchesError && !xeroMatchesLoading && !match && (
                    <div style={{ fontSize: 11, color: '#a1a1aa' }}>No Xero data for this subscription.</div>
                  )}

                  {match && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                        <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px' }}>
                          <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase' }}>Match</div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a', marginTop: 2 }}>
                            {match.match_type === 'none' ? 'no match' : match.match_type} · {match.confidence}
                          </div>
                        </div>
                        <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px' }}>
                          <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase' }}>Xero contact</div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a', marginTop: 2 }}>
                            {match.matched_xero_contact || '--'}
                          </div>
                        </div>
                        <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px' }}>
                          <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase' }}>Expected ({match.smart_window_days}d, AUD)</div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                            A${Number(match.expected_window_aud || 0).toFixed(2)}
                          </div>
                        </div>
                        <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px' }}>
                          <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase' }}>Actual ({match.smart_window_days}d, AUD)</div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                            A${Number(match.actual_window_aud || 0).toFixed(2)}
                          </div>
                        </div>
                        <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px' }}>
                          <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase' }}>Variance</div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: match.variance_pct == null ? '#a1a1aa' : (Math.abs(match.variance_pct) <= 10 ? '#16a34a' : Math.abs(match.variance_pct) <= 25 ? '#b45309' : '#dc2626'), marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                            {match.variance_pct == null ? '—' : `${match.variance_pct > 0 ? '+' : ''}${Number(match.variance_pct).toFixed(1)}%`}
                          </div>
                        </div>
                        <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px' }}>
                          <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase' }}>Latest charge</div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a', marginTop: 2 }}>
                            {match.matched_xero_latest_date ? fmtDate(match.matched_xero_latest_date) : '--'}
                          </div>
                        </div>
                      </div>

                      {match.conflicts && match.conflicts.length > 0 && (
                        <div style={{ fontSize: 11, color: '#991b1b', background: '#fde2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                          <div style={{ fontWeight: 600, marginBottom: 2 }}>Conflict — needs Hope review</div>
                          {match.conflicts.includes('multi_sub') && <div>• One Xero contact matches multiple active subscriptions. Cannot auto-attribute.</div>}
                          {match.conflicts.includes('generic_processor') && <div>• Vendor name is a generic payment processor (Claude / Google / Paddle.net / PayPal / Stripe / AMEX). Cannot auto-approve.</div>}
                        </div>
                      )}

                      {match.xero_transactions && match.xero_transactions.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#52525b', marginBottom: 6 }}>Top {Math.min(5, match.xero_transactions.length)} Xero transactions</div>
                          {match.xero_transactions.slice(0, 5).map(tx => {
                            const approved = isAlreadyApproved(drawerEvents, tx.bank_transaction_id);
                            const canApprove = canWrite && !approved && !XERO_MATCH_READONLY && (!match.conflicts || match.conflicts.length === 0);
                            return (
                              <div key={tx.bank_transaction_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #fafafa', fontSize: 11 }}>
                                <span style={{ color: '#0a0a0a', fontWeight: 500, fontVariantNumeric: 'tabular-nums', minWidth: 90 }}>
                                  {tx.currency} {Number(tx.amount || 0).toFixed(2)}
                                </span>
                                <span style={{ color: '#52525b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {tx.contact_name}{tx.reference ? ` · ${tx.reference}` : ''}
                                </span>
                                <span style={{ color: '#a1a1aa', flexShrink: 0 }}>{fmtDate(tx.date)}</span>
                                {approved && (
                                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: '#dcfce7', color: '#166534' }}>✓ approved</span>
                                )}
                                {canApprove && (
                                  <button
                                    onClick={async () => {
                                      // Double-check dedupe at click time (drawerEvents may have
                                      // been refreshed since render).
                                      if (isAlreadyApproved(drawerEvents, tx.bank_transaction_id)) {
                                        toast.error('Already approved', 'This Xero transaction is already attached to this subscription');
                                        return;
                                      }
                                      const fxRate = Number(fxRates[tx.currency]) || 1;
                                      const audEquivalent = Number(tx.amount) * fxRate;
                                      const noteParts = [`Xero match: bank_transaction_id=${tx.bank_transaction_id}`, `contact=${tx.contact_name}`, `date=${tx.date}`];
                                      if (tx.reference) noteParts.push(`ref=${tx.reference}`);
                                      try {
                                        await authWrite(session, 'subscription_events', 'insert', {
                                          subscription_id: drawer.id,
                                          event_type: 'charge',
                                          event_date: tx.date,
                                          amount_at_event: Number(tx.amount),
                                          currency_at_event: tx.currency,
                                          fx_rate_to_aud_at_event: fxRate,
                                          aud_equivalent_at_event: audEquivalent,
                                          note: noteParts.join(' · '),
                                          actor_code: session?.code || null,
                                        });
                                      } catch (e) {
                                        const { title, body } = humanizeError(e, 'Could not approve Xero match');
                                        toast.error(title, body);
                                        return;
                                      }
                                      const { data } = await aws.from('subscription_events').select('*').eq('subscription_id', drawer.id).order('created_at', { ascending: false });
                                      setDrawerEvents(data || []);
                                      clearXeroMatchCache();
                                      toast.success(`Approved Xero charge: ${tx.currency} ${Number(tx.amount).toFixed(2)}`);
                                    }}
                                    style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', cursor: 'pointer', fontFamily: 'inherit' }}
                                  >Approve as charge</button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {match.suggested_estimate_native != null && canWrite && !XERO_MATCH_READONLY && !isUnsafeMatchSuggestion(match, drawer) && match.variance_pct != null && Math.abs(match.variance_pct) > 10 && (!match.conflicts || match.conflicts.length === 0) && (
                        <button
                          onClick={() => {
                            // Open Edit modal pre-filled with the suggested estimate.
                            // User MUST click Save to actually apply — no auto-save.
                            const suggested = Number(match.suggested_estimate_native);
                            setNewSub({
                              ...drawer,
                              _isNew: false,
                              _original: { ...drawer },
                              amount: suggested.toFixed(2),
                            });
                            setDrawer(null);
                          }}
                          style={{ fontSize: 11, padding: '6px 12px', borderRadius: 6, border: '1px solid #e4e4e7', background: '#fff', color: '#0a0a0a', cursor: 'pointer', fontFamily: 'inherit', width: '100%' }}
                        >
                          Adopt suggested estimate: {drawer.currency} {Number(match.suggested_estimate_native).toFixed(2)} (currently {drawer.currency} {Number(drawer.amount).toFixed(2)})
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* Actual charges (Phase C, 2026-05-28).
                Lists subscription_events where event_type='charge'. Latest
                first. Shows the most recent charge + month-to-date total
                in AUD-equivalent (uses aud_equivalent_at_event when set). */}
            {(() => {
              const charges = drawerEvents.filter(ev => ev.event_type === 'charge');
              const ymNow = new Date().toISOString().slice(0, 7);
              const thisMonth = charges.filter(ev => (ev.event_date || '').slice(0, 7) === ymNow);
              const thisMonthAud = thisMonth.reduce((s, ev) => s + (Number(ev.aud_equivalent_at_event) || 0), 0);
              const latest = charges[0];
              return (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a' }}>Actual charges ({charges.length})</div>
                    {canWrite && (
                      <button
                        onClick={() => setChargeForm({
                          event_date: new Date().toISOString().slice(0, 10),
                          amount: '',
                          currency: drawer.currency || 'AUD',
                          payment_method: drawer.payment_method || 'amex_business',
                          note: ''
                        })}
                        style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #e4e4e7', background: '#fff', color: '#0a0a0a', cursor: 'pointer', fontFamily: 'inherit' }}
                      >+ Add charge</button>
                    )}
                  </div>
                  {(latest || thisMonth.length > 0) && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                      <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px' }}>
                        <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase' }}>Latest charge</div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a', marginTop: 2 }}>
                          {latest ? `${latest.currency_at_event || ''} ${Number(latest.amount_at_event).toFixed(2)} · ${fmtDate(latest.event_date)}` : '--'}
                        </div>
                      </div>
                      <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px' }}>
                        <div style={{ fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase' }}>Total this month (AUD)</div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#0a0a0a', marginTop: 2 }}>
                          {thisMonth.length > 0 ? `A$${thisMonthAud.toFixed(2)}` : '--'}
                        </div>
                      </div>
                    </div>
                  )}
                  {charges.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#a1a1aa' }}>No charges logged yet.</div>
                  ) : (
                    charges.map(ev => (
                      <div key={ev.id} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid #fafafa', fontSize: 11 }}>
                        <span style={{ color: '#0a0a0a', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{ev.currency_at_event || ''} {Number(ev.amount_at_event || 0).toFixed(2)}</span>
                        <span style={{ color: '#52525b', flex: 1 }}>{ev.note || ''}</span>
                        <span style={{ color: '#a1a1aa', flexShrink: 0 }}>{fmtDate(ev.event_date)}</span>
                      </div>
                    ))
                  )}
                </div>
              );
            })()}

            {/* Comments & activity (Phase B, 2026-05-28).
                Unified Asana-style timeline: user comments + system events
                + charges + edit-diffs, newest-first. User comments render
                as cards; system events render as compact subdued chips. */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a', marginBottom: 8 }}>Comments & activity</div>
              {canWrite && (
                <div style={{ marginBottom: 12 }}>
                  <textarea
                    className="form-textarea"
                    rows={2}
                    placeholder="Add a comment…"
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    style={{ width: '100%', fontSize: 13, padding: 8, fontFamily: 'inherit' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                    <button
                      onClick={async () => {
                        const text = commentText.trim();
                        if (!text) return;
                        try {
                          await authWrite(session, 'subscription_events', 'insert', {
                            subscription_id: drawer.id,
                            event_type: 'comment',
                            event_date: new Date().toISOString().slice(0, 10),
                            note: text,
                            actor_code: session?.code || null
                          });
                        } catch (e) {
                          const { title, body } = humanizeError(e, 'Could not post comment');
                          toast.error(title, body);
                          return;
                        }
                        setCommentText('');
                        const { data } = await aws.from('subscription_events').select('*').eq('subscription_id', drawer.id).order('created_at', { ascending: false });
                        setDrawerEvents(data || []);
                      }}
                      disabled={!commentText.trim()}
                      style={{ fontSize: 11, padding: '4px 14px', borderRadius: 6, border: 'none', background: commentText.trim() ? '#7c3aed' : '#e4e4e7', color: '#fff', cursor: commentText.trim() ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}
                    >Post comment</button>
                  </div>
                </div>
              )}
              {drawerEvents.length === 0 ? (
                <div style={{ fontSize: 11, color: '#a1a1aa' }}>No activity yet.</div>
              ) : drawerEvents.map(ev => {
                if (ev.event_type === 'comment') {
                  const author = staff.find(u => u.code === ev.actor_code)?.name || ev.actor_code || 'Unknown';
                  return (
                    <div key={ev.id} style={{ background: '#f4f4f5', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: '#52525b', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 600 }}>{author}</span>
                        <span>{fmtDateTime(ev.created_at)}</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#0a0a0a', whiteSpace: 'pre-wrap' }}>{ev.note || ''}</div>
                    </div>
                  );
                }
                if (ev.event_type === 'charge') {
                  return (
                    <div key={ev.id} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid #fafafa', fontSize: 11 }}>
                      <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 999, background: '#dcfce7', color: '#166534', flexShrink: 0 }}>charge</span>
                      <span style={{ color: '#0a0a0a', fontWeight: 500, flex: 1, fontVariantNumeric: 'tabular-nums' }}>
                        {ev.currency_at_event || ''} {Number(ev.amount_at_event || 0).toFixed(2)}{ev.note ? ` · ${ev.note}` : ''}
                      </span>
                      <span style={{ color: '#a1a1aa', flexShrink: 0 }}>{fmtDate(ev.event_date)}</span>
                    </div>
                  );
                }
                // System events: created, cancelled, edit_<field>, deleted, etc.
                const label = ev.event_type.replace(/^edit_/, '').replace(/_/g, ' ');
                return (
                  <div key={ev.id} style={{ display: 'flex', gap: 8, padding: '6px 0', fontSize: 11 }}>
                    <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 999, background: '#f4f4f5', color: '#52525b', flexShrink: 0 }}>{label}</span>
                    <span style={{ color: '#52525b', flex: 1 }}>{ev.note || '--'}</span>
                    <span style={{ color: '#a1a1aa', flexShrink: 0 }}>{fmtDate(ev.event_date)}</span>
                  </div>
                );
              })}
            </div>

            {/* Actions — Edit / Mark as cancelled / Delete record.
                Mo, 2026-05-28: "Mark as cancelled" is an internal status
                change (status='cancelled', does NOT touch the vendor).
                "Delete record" removes the dashboard row + event timeline
                (cascade), still does NOT touch the vendor. */}
            {canWrite && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  onClick={() => {
                    // Stash the pre-edit snapshot in _original so saveSub can
                    // compute per-field diffs and write audit events. The
                    // saveSub handler already strips _isNew + id + timestamps
                    // before update; it also strips _original (added 2026-05-28).
                    // Defensive normalize: ensure amount fields are
                    // controlled strings even if the DB returned null
                    // or a numeric type — React warns on undefined.
                    setNewSub({
                      ...drawer,
                      _isNew: false,
                      _original: { ...drawer },
                      amount: drawer.amount != null ? String(drawer.amount) : '',
                      amount_min: drawer.amount_min != null ? String(drawer.amount_min) : '',
                      amount_max: drawer.amount_max != null ? String(drawer.amount_max) : '',
                    });
                    setDrawer(null);
                  }}
                  style={{ padding: '8px 16px', fontSize: 12, fontWeight: 500, borderRadius: 8, border: '1px solid #e4e4e7', background: '#fff', color: '#0a0a0a', cursor: 'pointer', fontFamily: 'inherit', width: '100%' }}
                >Edit</button>

                {drawer.status !== 'cancelled' && (
                  <button onClick={async () => {
                    const reason = prompt('Why are you marking this as cancelled?\n\n(Internal record only — this does NOT cancel the subscription with the vendor.)');
                    if (!reason) return;
                    const today = new Date().toISOString().slice(0, 10);
                    try {
                      // Mark cancelled (status), then write the event row.
                      // If the second write fails the first has already
                      // committed — minor inconsistency we accept (vs. the
                      // alternative of an RPC for two rows).
                      await authWrite(session, 'subscriptions', 'update',
                        { status: 'cancelled', cancelled_on: today },
                        { column: 'id', value: drawer.id });
                      await authWrite(session, 'subscription_events', 'insert',
                        { subscription_id: drawer.id, event_type: 'cancelled', event_date: today, note: reason, actor_code: session?.code });
                    } catch (e) {
                      const { title, body } = humanizeError(e, 'Could not mark as cancelled');
                      toast.error(title, body);
                      return;
                    }
                    setDrawer(null); loadSubs();
                    toast.success('Marked as cancelled (internal record)');
                  }} style={{ padding: '8px 16px', fontSize: 12, fontWeight: 500, borderRadius: 8, border: '1px solid #fde68a', background: '#fffbeb', color: '#b45309', cursor: 'pointer', fontFamily: 'inherit', width: '100%' }}>Mark as cancelled</button>
                )}

                <button
                  onClick={() => setDeleteConfirm(drawer)}
                  style={{ padding: '8px 16px', fontSize: 12, fontWeight: 500, borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontFamily: 'inherit', width: '100%' }}
                >Delete record</button>
              </div>
            )}
          </div>
        </aside>
      </>)}

      {/* Add/Edit modal */}
      {/* CSV Preview Modal */}
      {csvPreview && createPortal(<>
        <div onClick={() => setCsvPreview(null)} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }} />
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 201, width: 600, maxWidth: '90vw', maxHeight: '80vh', background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e4e4e7' }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Import {csvPreview.length} Subscriptions</div>
            <div style={{ fontSize: 12, color: '#a1a1aa', marginTop: 2 }}>Review before importing. All will be added as active.</div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 24px' }}>
            {csvPreview.map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #fafafa', fontSize: 12 }}>
                <span style={{ fontWeight: 500, color: '#0a0a0a' }}>{r.vendor_name}</span>
                <span style={{ color: '#52525b' }}>{r.currency} {Number(r.amount).toFixed(2)} / {r.billing_cycle}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '12px 24px', borderTop: '1px solid #e4e4e7', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setCsvPreview(null)}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={csvImporting} onClick={importCsv}>{csvImporting ? 'Importing...' : `Import ${csvPreview.length} subs`}</button>
          </div>
        </div>
      </>, document.body)}

      {newSub && (<>
        <div onClick={() => setNewSub(null)} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }} />
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 201, width: 520, maxWidth: '90vw', maxHeight: '85vh', background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e4e4e7', fontSize: 16, fontWeight: 700 }}>{newSub._isNew ? 'Add Subscription' : 'Edit Subscription'}</div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            <div className="form-group"><label className="form-label">Vendor Name</label><input className="form-input" value={newSub.vendor_name} onChange={e => setNewSub(f => ({ ...f, vendor_name: e.target.value }))} autoFocus /></div>
            <div className="form-group"><label className="form-label">Description</label><input className="form-input" value={newSub.product_description || ''} onChange={e => setNewSub(f => ({ ...f, product_description: e.target.value }))} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group"><label className="form-label">Category</label><select className="form-select" value={newSub.category} onChange={e => setNewSub(f => ({ ...f, category: e.target.value }))}>{CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Status</label><select className="form-select" value={newSub.status} onChange={e => setNewSub(f => ({ ...f, status: e.target.value }))}>{['active', 'trial', 'paused', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}</select></div>
            </div>
            {/* Variable cost toggle hoisted above the Amount row so the
                Amount inputs reflect the choice immediately. */}
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label className="form-label" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={newSub.variable_cost} onChange={e => setNewSub(f => ({ ...f, variable_cost: e.target.checked }))} style={{ marginRight: 6 }} />
                Variable cost (usage-based)
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group">
                <label className="form-label">{newSub.variable_cost ? 'Amount range' : 'Amount'}</label>
                {newSub.variable_cost ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input className="form-input" type="number" step="0.01" placeholder="Min"
                      style={{ flex: 1, minWidth: 0, borderColor: parseFloat(newSub.amount_min) > 0 ? undefined : '#fca5a5' }}
                      value={newSub.amount_min || ''} onChange={e => setNewSub(f => ({ ...f, amount_min: e.target.value }))} />
                    <span style={{ color: '#a1a1aa', fontSize: 12, flexShrink: 0 }}>–</span>
                    <input className="form-input" type="number" step="0.01" placeholder="Max"
                      style={{ flex: 1, minWidth: 0, borderColor: (parseFloat(newSub.amount_max) > 0 && parseFloat(newSub.amount_max) >= parseFloat(newSub.amount_min)) ? undefined : '#fca5a5' }}
                      value={newSub.amount_max || ''} onChange={e => setNewSub(f => ({ ...f, amount_max: e.target.value }))} />
                  </div>
                ) : (
                  <input className="form-input" type="number" step="0.01" placeholder="0.00"
                    style={parseFloat(newSub.amount) > 0 ? undefined : { borderColor: '#fca5a5' }}
                    value={newSub.amount} onChange={e => setNewSub(f => ({ ...f, amount: e.target.value }))} />
                )}
              </div>
              <div className="form-group"><label className="form-label">Currency</label><select className="form-select" value={newSub.currency} onChange={e => setNewSub(f => ({ ...f, currency: e.target.value }))}>{CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Cycle</label><select className="form-select" value={newSub.billing_cycle} onChange={e => setNewSub(f => ({ ...f, billing_cycle: e.target.value }))}>{CYCLES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Payment Method</label>
              <select className="form-select" value={newSub.payment_method} onChange={e => setNewSub(f => ({ ...f, payment_method: e.target.value }))}>{PAYMENTS.map(p => <option key={p} value={p}>{PAY_LABELS[p]}</option>)}</select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group"><label className="form-label">Next Renewal</label><input className="form-input" type="date" value={newSub.next_renewal_date || ''} onChange={e => setNewSub(f => ({ ...f, next_renewal_date: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Started On</label><input className="form-input" type="date" value={newSub.started_on || ''} onChange={e => setNewSub(f => ({ ...f, started_on: e.target.value }))} /></div>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={!!newSub.auto_renew} onChange={e => setNewSub(f => ({ ...f, auto_renew: e.target.checked }))} style={{ marginRight: 6 }} />
                Auto-renew
              </label>
            </div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={newSub.notes || ''} onChange={e => setNewSub(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <div style={{ padding: '12px 24px', borderTop: '1px solid #e4e4e7', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setNewSub(null)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={saveSub}
              disabled={!isAmountValid(newSub)}
              title={isAmountValid(newSub) ? undefined : (newSub?.variable_cost ? 'Min and Max amount required (Max ≥ Min, both > 0)' : 'Amount required (> 0)')}
            >Save</button>
          </div>
        </div>
      </>)}

      {/* Add charge modal (Phase C, 2026-05-28).
          Logs an actual payment as a subscription_events row with
          event_type='charge'. amount_at_event + currency_at_event are
          populated, plus an AUD-equivalent via fxRates. */}
      {chargeForm && createPortal(<>
        <div onClick={() => setChargeForm(null)} style={{ position: 'fixed', inset: 0, zIndex: 250, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }} />
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 251, width: 460, maxWidth: '90vw', background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e4e4e7', fontSize: 16, fontWeight: 700 }}>Add charge</div>
          <div style={{ padding: '16px 24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div className="form-group"><label className="form-label">Charge date</label><input className="form-input" type="date" value={chargeForm.event_date} onChange={e => setChargeForm(f => ({ ...f, event_date: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Currency</label><select className="form-select" value={chargeForm.currency} onChange={e => setChargeForm(f => ({ ...f, currency: e.target.value }))}>{CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div className="form-group"><label className="form-label">Amount</label><input className="form-input" type="number" step="0.01" placeholder="0.00" value={chargeForm.amount} onChange={e => setChargeForm(f => ({ ...f, amount: e.target.value }))} autoFocus /></div>
              <div className="form-group"><label className="form-label">Payment method</label><select className="form-select" value={chargeForm.payment_method} onChange={e => setChargeForm(f => ({ ...f, payment_method: e.target.value }))}>{PAYMENTS.map(p => <option key={p} value={p}>{PAY_LABELS[p]}</option>)}</select></div>
            </div>
            <div className="form-group"><label className="form-label">Note</label><input className="form-input" placeholder="optional" value={chargeForm.note} onChange={e => setChargeForm(f => ({ ...f, note: e.target.value }))} /></div>
          </div>
          <div style={{ padding: '12px 24px', borderTop: '1px solid #e4e4e7', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setChargeForm(null)}>Cancel</button>
            <button
              className="btn btn-primary btn-sm"
              onClick={async () => {
                const amount = parseFloat(chargeForm.amount);
                if (isNaN(amount) || amount <= 0) { toast.error('Amount required', 'Enter a positive amount'); return; }
                const currency = chargeForm.currency || 'AUD';
                const fxRate = Number(fxRates[currency]) || 1;
                const audEquivalent = amount * fxRate;
                const noteParts = [];
                if (chargeForm.note?.trim()) noteParts.push(chargeForm.note.trim());
                if (chargeForm.payment_method) noteParts.push(`via ${PAY_LABELS[chargeForm.payment_method] || chargeForm.payment_method}`);
                try {
                  await authWrite(session, 'subscription_events', 'insert', {
                    subscription_id: drawer.id,
                    event_type: 'charge',
                    event_date: chargeForm.event_date || new Date().toISOString().slice(0, 10),
                    amount_at_event: amount,
                    currency_at_event: currency,
                    fx_rate_to_aud_at_event: fxRate,
                    aud_equivalent_at_event: audEquivalent,
                    note: noteParts.join(' '),
                    actor_code: session?.code || null
                  });
                } catch (e) {
                  const { title, body } = humanizeError(e, 'Could not log charge');
                  toast.error(title, body);
                  return;
                }
                setChargeForm(null);
                const { data } = await aws.from('subscription_events').select('*').eq('subscription_id', drawer.id).order('created_at', { ascending: false });
                setDrawerEvents(data || []);
                toast.success(`Charge logged: ${currency} ${amount.toFixed(2)}`);
              }}
            >Log charge</button>
          </div>
        </div>
      </>, document.body)}

      {/* Delete record confirmation modal — separate from drawer + newSub form.
          Mo, 2026-05-28: warning clearly states this is an internal-only record
          delete and does NOT cancel the subscription with the vendor. */}
      {deleteConfirm && createPortal(<>
        <div onClick={() => setDeleteConfirm(null)} style={{ position: 'fixed', inset: 0, zIndex: 250, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }} />
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 251, width: 440, maxWidth: '90vw', background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e4e4e7' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#dc2626' }}>Delete record</div>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <div style={{ fontSize: 14, color: '#0a0a0a', marginBottom: 8 }}>
              Delete the dashboard record for <strong>{deleteConfirm.vendor_name}</strong>?
            </div>
            <div style={{ fontSize: 12, color: '#52525b', lineHeight: 1.5 }}>
              This removes the subscription from this dashboard and clears its event timeline (cascade). It does <strong>NOT</strong> cancel the subscription with the vendor — you'll need to do that separately if required.
            </div>
          </div>
          <div style={{ padding: '12px 24px', borderTop: '1px solid #e4e4e7', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button
              style={{ background: '#dc2626', color: '#fff', padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}
              onClick={async () => {
                const deletedName = deleteConfirm.vendor_name;
                try {
                  await authWrite(session, 'subscriptions', 'delete', null,
                    { column: 'id', value: deleteConfirm.id });
                } catch (e) {
                  const { title, body } = humanizeError(e, 'Could not delete subscription record');
                  toast.error(title, body);
                  return;
                }
                setDeleteConfirm(null);
                setDrawer(null);
                loadSubs();
                toast.success(`Deleted ${deletedName} from dashboard`);
              }}
            >Delete record</button>
          </div>
        </div>
      </>, document.body)}
    </div>
  );
}
